import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { inviteLinksAPI, InviteValidationDto, RedeemInviteLinkResponseDto } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ActionRow, Button } from '../components';
import { useFormatDateTime } from '../i18n';

const extractPayload = <T,>(payload: T | { data?: T | { data?: T } }): T => {
  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    const wrapped = payload as { data?: T | { data?: T } };
    return (typeof wrapped.data === 'object' && wrapped.data !== null && 'data' in wrapped.data
      ? (wrapped.data as { data?: T }).data
      : wrapped.data) as T;
  }

  return payload as T;
};

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const responseData = (error as { response?: { data?: { detail?: string; error?: string; message?: string } } }).response?.data;
    return responseData?.detail || responseData?.error || responseData?.message || fallback;
  }

  return fallback;
};

export const PublicRedeemInvitePage: React.FC = () => {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<InviteValidationDto | null>(null);

  useEffect(() => {
    const loadInviteInfo = async () => {
      if (!token) {
        setError('Invalid invite link.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await inviteLinksAPI.validateInviteLink(token);
        setInviteInfo(extractPayload<InviteValidationDto>(response.data));
      } catch (inviteError) {
        setInviteInfo(null);
        setError(extractErrorMessage(inviteError, 'Failed to load invite information.'));
      } finally {
        setLoading(false);
      }
    };

    void loadInviteInfo();
  }, [token]);

  const redirectTarget = useMemo(() => `/invite/${token}`, [token]);

  const handleRedeemInvite = async () => {
    if (!token) {
      return;
    }

    try {
      setRedeeming(true);
      setError('');

      const response = await inviteLinksAPI.redeemInviteLink(token);
      const redeemResult = extractPayload<RedeemInviteLinkResponseDto>(response.data);
      const destinationTreeId = redeemResult.treeId || inviteInfo?.treeId;

      navigate(destinationTreeId ? `/canvas/${destinationTreeId}` : '/trees', {
        state: { success: redeemResult.message || 'Successfully joined the tree!' },
      });
    } catch (inviteError) {
      setError(extractErrorMessage(inviteError, 'Failed to redeem invite.'));
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="rounded-lg bg-white p-8 text-center shadow-xl">
          <p className="text-gray-600">{t('redeem.loading', { ns: 'invites' })}</p>
        </div>
      </div>
    );
  }

  if (error && !inviteInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl">
          <h1 className="mb-4 text-2xl font-bold text-red-600">{t('redeem.invalidTitle', { ns: 'invites' })}</h1>
          <p className="mb-6 text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/trees')}
            className="w-full rounded-lg bg-gray-500 px-6 py-3 font-medium text-white transition hover:bg-gray-600"
          >
            Back to My Trees
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-xl">
        <h1 className="mb-6 text-center text-3xl font-bold text-gray-900">{t('redeem.joinTitle', { ns: 'invites' })}</h1>

        <div className="mb-6 rounded-lg border-2 border-blue-200 bg-blue-50 p-6">
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">{inviteInfo?.treeName}</span>
            {' '}{' '}{t('redeem.invitedAs', { ns: 'invites' })}{' '}
            <span className="font-semibold text-gray-900">{inviteInfo?.role}</span>.
          </p>
          {inviteInfo?.expiresAt ? (
            <p className="mt-3 text-sm text-gray-500">
              {t('redeem.expires', { date: formatDateTime(inviteInfo.expiresAt), ns: 'invites' })}
            </p>
          ) : null}
        </div>

        {error ? (
          <div className="mb-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        ) : null}

        {!user ? (
          <div className="space-y-3">
            <p className="text-center text-gray-600">
              {t('redeem.loginRequired', { ns: 'invites' })}
            </p>
            <ActionRow className="sm:flex-col">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/register?redirect=${encodeURIComponent(redirectTarget)}`)}
                fullWidth
              >
                Create Account
              </Button>
              <Button
                type="button"
                onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirectTarget)}`)}
                fullWidth
              >
                Log In to Accept Invitation
              </Button>
            </ActionRow>
          </div>
        ) : (
          <ActionRow className="sm:flex-col">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/trees')}
              disabled={redeeming}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleRedeemInvite}
              disabled={redeeming}
              fullWidth
            >
              {redeeming ? t('redeem.accepting', { ns: 'invites' }) : t('redeem.accept', { ns: 'invites' })}
            </Button>
          </ActionRow>
        )}
      </div>
    </div>
  );
};
